import Axios from "axios";
import {
  CHECKOUT_ENDPOINT,
  COMPLETE_REGISTRATION_ENDPOINT,
  DONATE_SECRET_ENDPOINT,
  USER_ENDPOINT,
} from "Utils/Constants";

const API = process.env.REACT_APP_API_ENDPOINT;
const API_KEY = process.env.REACT_APP_API_KEY;
const IS_DEV = process.env.REACT_APP_NODE_ENV === "dev";
// CompleteRegistration can be pointed at a local SAM endpoint (e.g.
// http://localhost:3001) for local testing. Only honoured in dev, so a stray
// localhost value can never leak into a staging/production build.
const REGISTRATION_API =
  (IS_DEV && process.env.REACT_APP_REGISTRATION_API_ENDPOINT) || API;
// Same idea for CheckoutV2 (e.g. http://localhost:3002), so the charge and
// free-trial paths can be tested against a local SAM instance while recognition,
// the gift catalogue and /donate/secret keep using the deployed API — a local SAM
// only serves the single route it mounts. Dev-only, so a stray localhost value
// can never leak into a staging/production build.
const CHECKOUT_API =
  (IS_DEV && process.env.REACT_APP_CHECKOUT_API_ENDPOINT) || API;

/**
 * Shipping address for the thank-you gift. Address parts only — the recipient
 * name is filled server-side from the account name.
 */
export interface ShippingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

const CheckoutV2Api = {
  /**
   * On-blur recognition: whether the email already has an account and, if so,
   * whether its membership is active. Reads the deployed /user endpoint (plan +
   * ttl). A thrown lookup = new email. Active = has a plan whose term end (ttl,
   * unix seconds) is still in the future.
   */
  async getAccountStatus(
    email: string
  ): Promise<{
    exists: boolean;
    active: boolean;
    plan: string;
    amount?: number;
  }> {
    try {
      const res = await Axios.get(`${API}${USER_ENDPOINT}`, {
        // amount=true asks /user to also return the paid amount (Chargebee
        // plan_unit_price, cents) — used to report the donation amount for a
        // paid-but-not-registered donor completing their account.
        params: { email, amount: true },
        headers: API_KEY ? { "x-api-key": API_KEY } : undefined,
      });
      const data = res.data?.body ?? res.data ?? {};
      const plan = data.plan;
      const ttl = Number(data.ttl);
      // Match the app's own isMembershipInactive: a plan with no ttl (no term
      // end) counts as active; a ttl only makes it inactive once it's in the
      // past. Requiring a truthy ttl here would wrongly mark such members
      // inactive and route them to sign-in instead of the Active Membership page.
      const active = !!plan && (!ttl || ttl * 1000 > Date.now());
      const amount = Number(data.amount);
      return {
        exists: true,
        active,
        plan: plan || "",
        amount: Number.isFinite(amount) && amount > 0 ? amount : undefined,
      };
    } catch {
      return { exists: false, active: false, plan: "" };
    }
  },

  /**
   * Creates a Stripe PaymentIntent for the tokenized card + amount and returns
   * the client secret.
   */
  async getDonationSecret(
    paymentMethodId: string,
    amountCents: number
  ): Promise<string> {
    const res = await Axios.get(
      `${API}${DONATE_SECRET_ENDPOINT}?payment_method=${paymentMethodId}&amount=${amountCents}&dev=${IS_DEV}`
    );
    if (res.data?.statusCode !== 200) {
      throw new Error(
        res.data?.message ||
          res.data?.body?.message ||
          "Could not start payment"
      );
    }
    return res.data.body.secret as string;
  },

  /**
   * Posts the donation to the CheckoutV2 lambda. Charges the payment; when a
   * `password` is present it also creates the Auth0 account. In the current flow
   * we call it WITHOUT a password (charge + DynamoDB record only).
   */
  async postCheckout(payload: Record<string, unknown>): Promise<any> {
    const res = await Axios.post(`${CHECKOUT_API}${CHECKOUT_ENDPOINT}/v2`, payload);
    return res.data;
  },

  /**
   * Starts a PayPal Express Checkout. The lambda returns a PayPal URL to send the
   * donor to; PayPal then redirects back to the /paypal return page, which
   * completes the donation. Mirrors the legacy /checkout/generate-paypal call.
   */
  async generatePaypal(
    payload: Record<string, unknown>
  ): Promise<{ url?: string }> {
    const res = await Axios.post(
      `${API}${CHECKOUT_ENDPOINT}/generate-paypal`,
      payload
    );
    return res.data ?? {};
  },

  /**
   * Whether the email has an Auth0 LOGIN (distinct from a DynamoDB record).
   * Lets recognition tell "has a login → sign in" from "paid but not registered
   * → finish registration". Returns null if the check endpoint isn't available
   * yet (not deployed) so the caller can fall back to the Dynamo check.
   */
  async checkAuth0Login(email: string): Promise<boolean | null> {
    try {
      const res = await Axios.post(
        `${REGISTRATION_API}${COMPLETE_REGISTRATION_ENDPOINT}`,
        { email, check: true }
      );
      const data = res.data?.body ?? res.data;
      return !!data?.auth0Exists;
    } catch {
      return null;
    }
  },

  /**
   * Resolves an account-completion link (`/?sub_id=…`) to the donation behind it.
   * The link carries only the subscription id, so the email — which the Thank You
   * step needs to submit anything — has to come from the backend, along with
   * whether the donor already has a login and already submitted their details.
   * Returns null when the id doesn't resolve, so a stale or mistyped link simply
   * does nothing.
   */
  async lookupSubscription(subscriptionId: string): Promise<{
    email: string;
    subscriptionId: string;
    /** True once the donor has completed the Thank You form (their login exists). */
    hasAccount: boolean;
    /** Donation amount in cents — drives the gift minimums. */
    amount?: number;
    /** "one-time" | "monthly" — gift minimums differ per frequency. */
    donationType?: string;
  } | null> {
    try {
      const res = await Axios.post(
        `${REGISTRATION_API}${COMPLETE_REGISTRATION_ENDPOINT}`,
        { subscriptionId, lookup: true }
      );
      const data = res.data?.body ?? res.data;
      return data?.found ? data : null;
    } catch {
      return null;
    }
  },

  /**
   * Creates the Auth0 login for a paid donor (Thank You step). No charge. When a
   * `gift` (Chargebee addon id) and/or `shippingAddress` are provided, the lambda
   * also attaches them to the donor's subscription (no shipping fee). The
   * shipping recipient name is filled from firstName/lastName server-side, so the
   * address carries only address parts.
   */
  async completeRegistration(args: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    gift?: string;
    shippingAddress?: ShippingAddress;
    /** Subscription the donation just created (see attachGiftAndShipping). */
    subscriptionId?: string;
  }): Promise<any> {
    const res = await Axios.post(
      `${REGISTRATION_API}${COMPLETE_REGISTRATION_ENDPOINT}`,
      args
    );
    return res.data?.body ?? res.data;
  },

  /**
   * Existing member confirming their Thank You details: attaches the thank-you
   * gift addon + shipping address to their subscription. Same endpoint as
   * completeRegistration, in attach-only mode — no account creation, no charge.
   */
  async attachGiftAndShipping(args: {
    email: string;
    /** Recipient name for shipping_address (Chargebee requires it). */
    firstName?: string;
    lastName?: string;
    gift?: string;
    shippingAddress?: ShippingAddress;
    /**
     * The subscription this donation created, read back from the checkout
     * response. Chargebee's list endpoints are eventually consistent, so without
     * it the lambda's email lookup can find nothing this soon after checkout and
     * the gift is dropped. The lambda verifies the subscription's owner email.
     */
    subscriptionId?: string;
  }): Promise<any> {
    const res = await Axios.post(
      `${REGISTRATION_API}${COMPLETE_REGISTRATION_ENDPOINT}`,
      { ...args, attachOnly: true }
    );
    return res.data?.body ?? res.data;
  },
};

export default CheckoutV2Api;
